/**
 * Hosting Web Audio Modules — the one plugin format a browser can run.
 *
 * A WAM 2.0 plugin is an ES module at a URL whose default export creates an
 * instance on an audio context: an `AudioWorkletNode` carrying the plugin's
 * DSP, a parameter list the plugin describes itself, and an interface it
 * draws. Hosting one is three steps: the host environment is installed into
 * the context's worklet once, the module is imported, and an instance is
 * made. Every context — the live one, and the one each offline render makes
 * — gets its own environment and its own instance, exactly as it gets its own
 * worklet modules.
 *
 * The plugin's code is fetched from its URL. That is the one thing in this
 * application that reaches out of the machine, and it happens only when a
 * user gives a URL to an insert; the audio still goes nowhere. The SDK itself
 * is imported lazily, so a project with no plugins never loads it.
 */
import type { WamParameterInfoMap, WebAudioModule as IWebAudioModule, WamNode as IWamNode } from '@webaudiomodules/api';
import type { WamParameterInfo } from '@/domain/types';

/** A plugin loaded onto a context: its node and what it says about itself. */
export interface LoadedWam {
  instance: IWebAudioModule;
  node: IWamNode;
  name: string;
  parameters: WamParameterInfo[];
  /** How much later than its input the plugin's output arrives, in seconds; what compensation lines up. */
  latencySec: number;
}

/** URL schemes a plugin may be fetched from. `file:` is for tests; a browser refuses it anyway. */
export function isWamUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' || url.protocol === 'file:';
  } catch {
    return false;
  }
}

type HostInit = Promise<[groupId: string, groupKey: string]>;
const hosts = new WeakMap<BaseAudioContext, HostInit>();

/** The host environment for a context, installed once per context. */
function hostFor(ctx: BaseAudioContext): HostInit {
  let pending = hosts.get(ctx);
  if (!pending) {
    pending = import('@webaudiomodules/sdk').then(({ initializeWamHost }) => initializeWamHost(ctx));
    hosts.set(ctx, pending);
    pending.catch(() => {
      if (hosts.get(ctx) === pending) hosts.delete(ctx);
    });
  }
  return pending;
}

/** Cached imports: a plugin's module is fetched once per URL, however many inserts use it. */
const modules = new Map<string, Promise<{ default: typeof IWebAudioModule }>>();

function moduleFor(url: string): Promise<{ default: typeof IWebAudioModule }> {
  let pending = modules.get(url);
  if (!pending) {
    pending = import(/* @vite-ignore */ url).then((mod: { default?: unknown }) => {
      const plugin = mod.default as typeof IWebAudioModule | undefined;
      if (typeof plugin !== 'function' || typeof plugin.createInstance !== 'function') {
        throw new Error('That module is not a Web Audio Module: it has no default export with createInstance().');
      }
      return { default: plugin };
    });
    modules.set(url, pending);
    pending.catch(() => {
      if (modules.get(url) === pending) modules.delete(url);
    });
  }
  return pending;
}

function describe(info: WamParameterInfoMap): WamParameterInfo[] {
  return Object.values(info)
    .filter((p) => p.type === 'float' || p.type === 'int')
    .map((p) => ({
      id: p.id,
      label: p.label || p.id,
      min: p.minValue,
      max: p.maxValue,
      default: p.defaultValue,
      units: p.units || '',
    }));
}

/** Loads a plugin from its URL onto a context and makes an instance of it. */
export async function loadWam(ctx: BaseAudioContext, url: string): Promise<LoadedWam> {
  if (!isWamUrl(url)) throw new Error('A plugin URL must be http, https or file.');
  const [groupId] = await hostFor(ctx);
  const { default: Plugin } = await moduleFor(url);
  const instance = await Plugin.createInstance(groupId, ctx);
  const node = instance.audioNode;
  if (!node) throw new Error('The plugin made no audio node.');
  const info = await node.getParameterInfo();
  const descriptor = instance.descriptor as { name?: string } | undefined;
  // In samples on the wire; a plugin that answers nothing sensible has none.
  let latencySamples = 0;
  try {
    const answer = await node.getCompensationDelay();
    if (Number.isFinite(answer) && answer > 0) latencySamples = answer;
  } catch {
    latencySamples = 0;
  }
  return {
    instance,
    node,
    name: (descriptor?.name || instance.name || 'Web Audio Module').slice(0, 128),
    parameters: describe(info),
    latencySec: latencySamples / ctx.sampleRate,
  };
}
